/**
 * @biomax/plugin-comm-models — communication + risk-perception +
 * adherence-decay models.
 *
 * NPI compliance is not constant. A masking mandate that achieves
 * 90% compliance day 1 may erode to 60% by week 12 (weariness,
 * polarisation, normalisation). This plugin models that erosion +
 * the risk-perception inputs that drive it.
 *
 * v0.1 scope:
 *   - Adherence-decay curve: adherence(t) = baseline +
 *     (initial - baseline) × exp(-λ × t)
 *   - Risk-perception modulation: adherence boosted when local cases
 *     rise (perceived risk ↑) and decays during low-incidence periods
 *   - Trust-index → adherence multiplier
 *   - composeAdherenceScaler — produces a BetaScaler compatible with
 *     simulateSir/Seir/Seird etc. for use as a behavioural β scaler
 *
 * Reference: Petherick et al. 2021 Nat Hum Behav — pandemic fatigue
 * documentation; West et al. 2020 Nat Med — adherence theory.
 */

import type { PluginManifest } from '@biomax/types';

export interface AdherenceCurveParams {
  /** Adherence on day 0 (immediately after activation), in [0,1]. */
  initial: number;
  /** Steady-state baseline adherence the curve decays towards. */
  baseline: number;
  /** Half-life of decay from initial → baseline, days. */
  halfLifeDays: number;
}

export interface RiskPerceptionParams {
  /**
   * Per-day case count series (e.g., from a simulation). Drives the
   * perceived-risk modulation.
   */
  dailyCases: number[];
  /**
   * Population total for normalising per-100k risk perception.
   */
  population: number;
  /**
   * Multiplier strength: how much adherence rebounds when cases rise.
   * 1.0 = double adherence at extreme cases; 0 = adherence ignores
   * cases entirely.
   */
  responseGain?: number;
}

/** Adherence at time t under exponential decay. */
export function adherenceAt(params: AdherenceCurveParams, t: number): number {
  if (params.halfLifeDays <= 0) return params.baseline;
  const decay = Math.log(2) / params.halfLifeDays;
  return params.baseline + (params.initial - params.baseline) * Math.exp(-decay * t);
}

/**
 * Adherence trajectory modulated by risk-perception (case count).
 * Returns one value per day in `dailyCases`.
 */
export function adherenceTrajectoryWithRisk(
  decay: AdherenceCurveParams, risk: RiskPerceptionParams,
): number[] {
  const out: number[] = [];
  const gain = risk.responseGain ?? 0.5;
  // Risk weight: cases per 100k, normalised by a soft saturation.
  for (let t = 0; t < risk.dailyCases.length; t++) {
    const base = adherenceAt(decay, t);
    const casesPer100k = (risk.dailyCases[t] ?? 0) / risk.population * 100_000;
    // Logistic-style boost: at 0 cases, boost=0; at very high cases, boost saturates.
    const riskBoost = gain * (casesPer100k / (casesPer100k + 100));
    out.push(Math.min(1, Math.max(0, base + riskBoost)));
  }
  return out;
}

/**
 * Compose an adherence trajectory into a BetaScaler compatible with
 * the compartmental simulators. βscale = 1 - adherence × intervention_effect.
 */
export function composeAdherenceBetaScaler(
  interventionEffect: number, // 0..1: what fraction β is reduced at full adherence
  trajectory: number[],
): (t: number) => number {
  return (t: number): number => {
    const idx = Math.max(0, Math.min(trajectory.length - 1, Math.floor(t)));
    const adherence = trajectory[idx]!;
    return 1 - adherence * interventionEffect;
  };
}

export interface TrustIndex {
  /** Government / institutional trust, 0..1 (1 = full trust). */
  government: number;
  /** Healthcare/clinician trust. */
  healthcare: number;
  /** Community / peer-network trust. */
  community: number;
  citations: { source: string; pmid?: string; note?: string }[];
}

/**
 * Compute an adherence multiplier from a trust index. Per West 2020
 * + Bavel 2020, intervention compliance scales roughly with sqrt of
 * trust-in-source-of-message.
 */
export function trustAdherenceMultiplier(t: TrustIndex, channel: 'gov' | 'clinical' | 'community'): number {
  const v = channel === 'gov' ? t.government
         : channel === 'clinical' ? t.healthcare
         : t.community;
  return Math.sqrt(Math.max(0, Math.min(1, v)));
}

/**
 * Citations for the adherence-decay + risk-perception models in this
 * plugin. PMIDs round-tripped via @biomax/plugin-literature-fetch on
 * the verifiedAt date. Re-run scripts/verify-references.ts to revalidate.
 */
export const COMM_MODEL_REFERENCES = [
  {
    id: 'petherick-2021',
    label: 'Petherick et al. 2021 Nat Hum Behav — "A worldwide assessment of changes in adherence to COVID-19 protective behaviours and hypothesized pandemic fatigue"',
    pmid: '34345009',
    doi: '10.1038/s41562-021-01181-x',
    appliesTo: 'AdherenceCurveParams (exponential decay)',
    note: 'Empirical evidence for adherence decay (pandemic fatigue) across 14 countries. Backs the exponential-decay form of adherenceAt().',
    verifiedAt: '2026-05-28',
  },
  {
    id: 'west-2020',
    label: 'West et al. 2020 Nat Hum Behav — "Applying principles of behaviour change to reduce SARS-CoV-2 transmission"',
    pmid: '32377018',
    doi: '10.1038/s41562-020-0887-9',
    appliesTo: 'AdherenceCurveParams (baseline)',
    note: 'Behaviour-change framework for sustained adherence. Provides the baseline-adherence anchor in our exponential-decay form.',
    verifiedAt: '2026-05-28',
  },
  {
    id: 'bavel-2020',
    label: 'Van Bavel et al. 2020 Nat Hum Behav — "Using social and behavioural science to support COVID-19 pandemic response"',
    pmid: '32355299',
    doi: '10.1038/s41562-020-0884-z',
    appliesTo: 'RiskPerceptionParams + trustAdherenceMultiplier',
    note: 'Foundational review of risk perception, trust, and adherence. Backs the responseGain and channel-trust modulators.',
    verifiedAt: '2026-05-28',
  },
] as const;

export const manifest: PluginManifest = {
  id: 'comm-models', version: '0.1.0',
  engineCompat: '^0.5.0', kingdoms: '*', systems: [], components: [],
};
